import { useRef, useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { unsafeOverflowAutoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/unsafe-overflow/element';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import invariant from 'tiny-invariant';

import {
  getColumnData,
  isCardData,
  isCardDropTargetData,
  isColumnData,
  isDraggingACard,
  isDraggingAColumn,
} from './data';
import { isSafari } from './isSafari';
import { isShallowEqual } from './isShallowEqual';
import { SettingsContext } from './settingsContext';

export const useBoardColumn = ({ column }) => {
  const [page, setPage] = useState(1);
  const [getData, { data, isLoading, isFetching, originalArgs }] = column.meta.query();
  const scrollableRef = useRef(null);
  const outerFullHeightRef = useRef(null);
  const headerRef = useRef(null);
  const innerRef = useRef(null);
  const idle = useMemo(() => ({ type: 'idle' }), []);
  const [state, setState] = useState(idle);
  const [isForceRefetching, setIsForceRefetching] = useState(false);
  const { settings } = useContext(SettingsContext);
  const cards = useMemo(
    () =>
      data?.ids?.map((contactId) => {
        const contact = data.entities[contactId];
        return {
          id: `card:${contact.id}`,
          contact,
        };
      }) || [],
    [data],
  );
  const cardsCount = useMemo(() => data?.meta?.queryTotal || 0, [data]);
  const extractedGlobalFilters = useMemo(() => {
    const { filters } = column.meta.argsForColumn || {};
    return JSON.stringify(filters || {});
  }, [column]);

  useEffect(() => {
    if (!(column.id !== 'column:unassigned' && !column.status)) {
      getData({ ...column.meta.argsForColumn, page }, false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
    getData({ ...column.meta.argsForColumn, page: 1 });
  }, [extractedGlobalFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const forceRefetch = useCallback(() => {
    const args = originalArgs ?? { ...column.meta.argsForColumn, page };

    setIsForceRefetching(true);
    return getData(args, false)
      .unwrap()
      .finally(() => {
        setIsForceRefetching(false);
      });
  }, [getData, originalArgs, column.meta.argsForColumn, page]);

  const onEndReached = useCallback(() => {
    if (isFetching || isLoading || data?.ids?.length >= cardsCount) return;

    if (Math.ceil(cardsCount / 20) === page && data?.ids?.length < cardsCount) {
      forceRefetch();
      return;
    }

    if (cardsCount <= 20) return;

    if (data?.ids?.length < page * 20) {
      forceRefetch().then(() => {
        setPage((prev) => prev + 1);
        getData({ ...column.meta.argsForColumn, page: page + 1 });
      });
    } else {
      setPage((prev) => prev + 1);
      getData({ ...column.meta.argsForColumn, page: page + 1 });
    }
  }, [isFetching, isLoading, data?.ids?.length, cardsCount, page, forceRefetch, getData, column.meta.argsForColumn]);

  useEffect(() => {
    const outer = outerFullHeightRef.current;
    const scrollable = scrollableRef.current;
    const header = headerRef.current;
    const inner = innerRef.current;
    invariant(outer);
    invariant(scrollable);
    invariant(header);
    invariant(inner);

    const columnData = getColumnData({ column });

    function setIsCardOver({ data: dragData, location }) {
      const innerMost = location.current.dropTargets[0];
      const isOverChildCard = Boolean(innerMost && isCardDropTargetData(innerMost.data));

      const proposed = {
        type: 'is-card-over',
        dragging: dragData.rect,
        isOverChildCard,
      };
      // optimization - don't update state if we don't need to.
      setState((current) => {
        if (isShallowEqual(proposed, current)) {
          return current;
        }
        return proposed;
      });
    }

    return combine(
      // Only make the column draggable if it's not the unassigned column
      ...(column.id !== 'column:unassigned'
        ? [
            draggable({
              element: header,
              getInitialData: () => columnData,
              onGenerateDragPreview({ source, location, nativeSetDragImage }) {
                const { data: sourceData } = source;
                invariant(isColumnData(sourceData));
                setCustomNativeDragPreview({
                  nativeSetDragImage,
                  getOffset: preserveOffsetOnSource({ element: header, input: location.current.input }),
                  render({ container }) {
                    // Simple drag preview generation: just cloning the current element.
                    // Not using react for this.
                    const rect = inner.getBoundingClientRect();
                    const preview = inner.cloneNode(true);
                    invariant(preview instanceof HTMLElement);
                    preview.style.width = `${rect.width}px`;
                    preview.style.height = `${rect.height}px`;

                    // rotation of native drag previews does not work in safari
                    if (!isSafari()) {
                      preview.style.transform = 'rotate(4deg)';
                    }

                    container.appendChild(preview);
                  },
                });
              },
              onDragStart() {
                setState({ type: 'is-dragging' });
              },
              onDrop() {
                setState(idle);
              },
            }),
          ]
        : []),
      dropTargetForElements({
        element: outer,
        getData: () => columnData,
        canDrop({ source }) {
          return isDraggingACard({ source }) || isDraggingAColumn({ source });
        },
        getIsSticky: () => true,
        onDragStart({ source, location }) {
          if (isCardData(source.data)) {
            setIsCardOver({ data: source.data, location });
          }
        },
        onDragEnter({ source, location }) {
          if (isCardData(source.data)) {
            setIsCardOver({ data: source.data, location });
            return;
          }
          if (isColumnData(source.data) && source.data.column.id !== column.id) {
            setState({ type: 'is-column-over' });
          }
        },
        onDropTargetChange({ source, location }) {
          if (isCardData(source.data)) {
            setIsCardOver({ data: source.data, location });
          }
        },
        onDragLeave({ source }) {
          if (isColumnData(source.data) && source.data.column.id === column.id) {
            return;
          }
          setState(idle);
        },
        onDrop() {
          setState(idle);
        },
      }),
      autoScrollForElements({
        canScroll({ source }) {
          if (!settings.isOverElementAutoScrollEnabled) {
            return false;
          }

          return isDraggingACard({ source });
        },
        getConfiguration: () => ({ maxScrollSpeed: settings.columnScrollSpeed }),
        element: scrollable,
      }),
      unsafeOverflowAutoScrollForElements({
        element: scrollable,
        getConfiguration: () => ({ maxScrollSpeed: settings.columnScrollSpeed }),
        canScroll({ source }) {
          if (!settings.isOverElementAutoScrollEnabled) {
            return false;
          }

          if (!settings.isOverflowScrollingEnabled) {
            return false;
          }

          return isDraggingACard({ source });
        },
        getOverflow() {
          return {
            forTopEdge: {
              top: 1000,
            },
            forBottomEdge: {
              bottom: 1000,
            },
          };
        },
      }),
    );
  }, [column, idle, settings]);

  return {
    state,
    outerFullHeightRef,
    scrollableRef,
    headerRef,
    innerRef,
    cards,
    cardsCount,
    isLoading,
    isFetching,
    isForceRefetching,
    page,
    onEndReached,
  };
};
